import { hexToRGBA } from "./utils"

const primitives = {
  greys: {
    200: "#F1F1F1", // backgroundLight
    300: "#EDEDED",
    400: "#F9F9F9",
    475: "#DBDBDB", // borderLight
    500: "#1D1D1D",
    550: "#C4C4C4",
    700: "#969696",
    800: "#767676",
    850: "#575757",
    900: "#1D1D1B", // primitiveGrey900
    1000: "#FAFAFA",
    1100: "#F6F6F6",
  },
  blues: {
    100: "#E5EEF3", // use for background on info banner
    200: "#E2E8F0", // used in codebase but not included in figma
    300: "#3399FF", // used in codebase but not included in figma
    400: "#71A9CE", // used in codebase but not included in figma
    600: "#4A7796", // used in codebase but not included in figma
  },
  oranges: {
    500: "#FFBF3F",
    600: "#FF5C01", // Taken from banner
  },
  yellows: {
    500: "#FFBF3F",
    600: "#FFF2D9", // used in codebase but not included in figma
  },
  pinks: {
    100: "#FFF6F8", // backgroundAccentTertiary
    150: "#F9EBE9",
    300: "#F6CED6", // backgroundAccentSecondary
    400: "#FAA4B3", // backgroundAccent
    450: "#FF8FA2", // backgroundAccentVariant
    900: "#F2B8C3",
    1000: "#FF7276", // backgroundAccentStrong
  },
  browns: {
    800: "#A1403D",
  },
  greens: {
    500: "#597F57",
    600: "#638E60",
    700: "#3A893E",
  },
  white: "#fff",
  black: "#000",
  reds: {
    100: "#AF272F", // used in codebase but not included in figma
    200: "#F0565A", // used in codebase but not included in figma
    300: "#ED0319", // used in codebase but not included in figma
  },
}

/* the previous palette re-ordered along a linear scale from light to dark  */

const neutrals = {
  0: primitives.white,
  100: primitives.greys[1000],
  200: primitives.greys[1100],
  300: primitives.greys[200],
  400: primitives.greys[300],
  500: primitives.greys[475],
  600: primitives.greys[550],
  700: primitives.greys[700],
  800: primitives.greys[800],
  900: primitives.greys[850],
  1000: primitives.greys[900],
  1100: primitives.black,
  1200: primitives.greys[400],
  1300: primitives.greys[500],
}

const COLOR = "color"
const SHADOW = "shadow"
const TYPOGRAPHY = "typography"

export const colorTokens = {
  content: {
    $type: COLOR,
    $value: neutrals[1100],
  },
  contentSecondary: {
    $type: COLOR,
    $value: neutrals[1000],
  },
  contentLight: {
    $type: COLOR,
    $value: neutrals[800],
  },
  contentLightest: {
    $type: COLOR,
    $value: neutrals[1200],
  },
  copyTextLight: {
    $type: COLOR,
    $value: neutrals[1300],
  },
  contentTertiary: {
    $type: COLOR,
    $value: neutrals[900],
  },
  contentInverse: {
    $type: COLOR,
    $value: neutrals[0],
  },
  contentInverseSecondary: {
    $type: COLOR,
    $value: neutrals[100],
  },
  contentInverseTertiary: {
    $type: COLOR,
    $value: neutrals[200],
  },
  border: {
    $type: COLOR,
    $value: neutrals[600],
  },
  borderTertiary: {
    $type: COLOR,
    $value: neutrals[500],
  },
  borderInverse: {
    $type: COLOR,
    $value: neutrals[900],
  },
  borderLight: {
    $type: COLOR,
    $value: primitives.greys[475],
  },
  borderLightAccent: {
    $type: COLOR,
    $value: primitives.greys[300],
  },
  borderSecondary: {
    $type: COLOR,
    $value: neutrals[1000],
  },
  borderLightSecondary: {
    $type: COLOR,
    $value: neutrals[400],
  },
  borderFocus: {
    $type: COLOR,
    $value: primitives.blues[600],
    $description:
      "Use for styling focus state on interactive elements for accessibility",
  },
  borderAccent: {
    $type: COLOR,
    $value: primitives.pinks[450],
    $description: "Use for separators",
  },
  borderAccentSecondary: {
    $type: COLOR,
    $value: primitives.pinks[300],
  },
  contentAccent: {
    $type: COLOR,
    $value: primitives.pinks[450],
    $description: "Use for separators",
  },
  contentAccentSecondary: {
    $type: COLOR,
    $value: primitives.yellows[500],
  },
  background: {
    $type: COLOR,
    $value: neutrals[0],
  },
  backgroundSecondary: {
    $type: COLOR,
    $value: neutrals[100],
  },
  backgroundTertiary: {
    $type: COLOR,
    $value: neutrals[200],
  },
  backgroundInverse: {
    $type: COLOR,
    $value: neutrals[1100],
  },
  backgroundInverseSecondary: {
    $type: COLOR,
    $value: neutrals[1000],
  },
  backgroundInverseTertiary: {
    $type: COLOR,
    $value: neutrals[900],
  },
  backgroundAccent: {
    $type: COLOR,
    $value: primitives.pinks[400],
  },
  backgroundAccentVariant: {
    $type: COLOR,
    $value: primitives.pinks[450],
  },
  backgroundAccentStrong: {
    $type: COLOR,
    $value: primitives.pinks[1000],
  },
  backgroundAccentSecondary: {
    $type: COLOR,
    $value: primitives.pinks[300],
  },
  backgroundAccentTertiary: {
    $type: COLOR,
    $value: primitives.pinks[100],
  },
  backgroundLight: {
    $type: COLOR,
    $value: primitives.greys[200],
  },
  contentError: {
    $type: COLOR,
    $value: primitives.browns[800],
  },
  backgroundError: {
    $type: COLOR,
    $value: primitives.pinks[150],
  },
  contentWarning: {
    $type: COLOR,
    $value: "#9E6900",
  },
  backgroundWarning: {
    $type: COLOR,
    $value: primitives.oranges[500],
  },
  backgroundOverlayDark: {
    $type: COLOR,
    $value: "#0000004c",
    $description: "30% black",
  },
  backgroundOverlayLight: {
    $type: COLOR,
    $value: "#00000014",
    $description: "8% black",
  },
  backgroundNotificationLight: {
    $type: COLOR,
    $value: primitives.blues[100],
  },
  borderNotificationLight: {
    $type: COLOR,
    $value: primitives.blues[400],
  },
  contentPositive: {
    $type: COLOR,
    $value: primitives.greens[500],
  },
  contentSucceed: {
    $type: COLOR,
    $value: primitives.greens[600],
  },
  contentNegative: {
    $type: COLOR,
    $value: primitives.browns[800],
  },
  contentNeutral: {
    $type: COLOR,
    $value: primitives.greys[800],
  },
  contentNeutralSecondary: {
    $type: COLOR,
    $value: neutrals[700],
  },
  contentRed: {
    $type: COLOR,
    $value: primitives.reds[200],
  },
  contentGreen: {
    $type: COLOR,
    $value: primitives.greens[700],
  },
  contentOrange: {
    $type: COLOR,
    $value: primitives.oranges[600],
  },
  contentDeepRed: {
    $type: COLOR,
    $value: primitives.reds[300],
  },
  hoverAccent: {
    $type: COLOR,
    $value: primitives.pinks[300],
  },
  primitiveGrey900: {
    $type: COLOR,
    $value: primitives.greys[900],
  },
}

const shadowTokens = {
  shadow1: {
    $type: SHADOW,
    $value: {
      color: "#00000032",
      offsetX: "0px",
      offsetY: "1px",
      blur: "2px",
      spread: "0",
    },
  },
  shadow2: {
    $type: SHADOW,
    $value: {
      color: "#1901341F",
      offsetX: "0px",
      offsetY: "2px",
      blur: "10px",
      spread: "0",
    },
  },
  shadow3: {
    $type: SHADOW,
    $value: {
      color: "#00000032",
      offsetX: "0px",
      offsetY: "0px",
      blur: "10px",
      spread: "0",
    },
  },
}
type ColorValues = keyof typeof colorTokens
type ColorTypes = {
  [key in ColorValues]: string
}

export const colors = Object.fromEntries(
  Object.entries(colorTokens).map(([k, { $value }]) => [k, $value])
) as ColorTypes

type ShadowValues = keyof typeof shadowTokens
type ShadowTypes = {
  [key in ShadowValues]: string
}

export const shadows = Object.fromEntries(
  Object.entries(shadowTokens).map(([k, { $value }]) => {
    const { offsetX, offsetY, blur, spread, color } = $value

    return [
      k,
      [offsetX, offsetY, blur, spread, hexToRGBA(color)]
        .filter((v) => v)
        .join(" "),
    ]
  })
) as ShadowTypes

export const spacings = {
  none: "0rem",
  xxsmall: "0.125rem", // 2px
  xsmall: "0.25rem", // 4px
  small: "0.5rem", // 8px
  medium: "1rem", // 16px
  large: "1.5rem", // 24px
  xlarge: "2rem", // 32px
  xxlarge: "3rem", // 48px
  xxxlarge: "4rem", // 64px
} as const

export const zIndexes = {
  textOverlay: "1",
} as const

export const maxWidths = {
  carouselTablet: "36.5rem",
  carouselMobile: "calc(100vw - 4rem)",
} as const

// lg breakpoint is 79.99rem in the new theme
const breakpoints = {
  xs: "22.99rem", // 367.84px
  sm: "34.99rem", // 559.84px
  md: "47.99rem", // 767.84px
  mdlg: "63.99rem", // 1023.84px
  lg: "79.99rem", // 1279.84px
  xl: "89.99rem", // 1439.84px
}

export const mediaQueries = {
  mobile: `screen and (max-width: ${breakpoints?.sm})`,
  tablet: `screen and (min-width: ${breakpoints?.md}) and (max-width: ${breakpoints?.lg})`,
  smallDesktop: `screen and (min-width: ${breakpoints?.md})`,
  mdDesktop: `screen and (min-width: ${breakpoints?.mdlg})`,
  desktop: `screen and (min-width: ${breakpoints?.lg})`,
  desktopLarge: `screen and (min-width: ${breakpoints?.xl})`,
}

export const font = {
  book: "var(--font-book)",
  demi: "var(--font-demi)",
  light: "var(--font-light)",
  medium: "var(--font-medium)",
  apercu: "var(--font-apercu)",
  acuminRegular: "var(--font-acumin-regular)",
  acuminLight: "var(--font-acumin-light)",
  acuminMedium: "var(--font-acumin-medium)",
  acuminBold: "var(--font-acumin-bold)",
  acuminBoldItalic: "var(--font-acumin-bolditalic)",
  acuminSemiBold: "var(--font-acumin-semibold)",
  boldOblique: "var(--font-bold-oblique)",
}

export const fontSize = {
  xxxsmall: "0.5rem", // 8px
  xxsmall: "0.625rem", // 10px
  xsmall: "0.75rem", // 12px
  small: "0.875rem", // 14px
  medium: "1rem", // 16px
  large: "1.125rem", // 18px
  xlarge: "1.5rem", // 24px
  xxlarge: "1.75rem", // 28px
  xxxlarge: "2rem", // 32px
}

export const lineHeight = {
  xxsmall: "0.875rem", // 14px
  xsmall: "1rem", // 16px
  small: "1.125rem", // 18px
  medium: "1.5rem", // 24px
  large: "1.75rem", // 28px
  xlarge: "2rem", // 32px
  xxlarge: "2.5rem", // 40px
  xxxlarge: "3rem", // 48px
}

export const fontWeight = {
  light: 300,
  medium: 400,
  semiBold: 500,
  bold: 600,
}

const typographyTokens = {
  headingXXLargeApperCu: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.apercu, // headline mobile nav big
      fontSize: fontSize.xxlarge,
      lineHeight: lineHeight.xxlarge,
    },
  },
  headingLargeApperCu: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.apercu, // headline mobile nav big
      fontSize: fontSize.large,
      lineHeight: lineHeight.large,
    },
  },
  headingXLarge: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminBold, // headline mobile nav big
      fontSize: fontSize.xlarge,
      lineHeight: lineHeight.xlarge,
      fontWeight: fontWeight.medium,
    },
  },
  headingLarge: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminBold, // headline mobile nav medium
      fontSize: fontSize.large,
      lineHeight: lineHeight.large,
      fontWeight: fontWeight.bold,
    },
  },
  headingLargeAlt: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminSemiBold, // headline mobile submenu
      fontSize: fontSize.large,
      lineHeight: lineHeight.xlarge,
      fontWeight: fontWeight.medium,
    },
  },
  headingMedium: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminMedium, // headline desktop nav
      fontSize: fontSize.large,
      lineHeight: lineHeight.large,
      fontWeight: fontWeight.semiBold,
    },
  },
  headingMediumApperCu: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.apercu, // headline mobile nav big
      fontSize: fontSize.medium,
      lineHeight: lineHeight.medium,
    },
  },
  headingSmall: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.apercu, // trustpilot widget headline
      fontSize: fontSize.medium,
      lineHeight: lineHeight.medium,
      fontWeight: fontWeight.medium,
    },
  },
  paragraphLarge: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminLight, // Nav Links Mobile
      fontSize: fontSize.large,
      lineHeight: lineHeight.large,
      fontWeight: fontWeight.medium,
    },
  },
  paragraphxxSmall: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminLight, // smallprint page bottom
      fontSize: fontSize.xxsmall,
      lineHeight: lineHeight.xxsmall,
    },
  },
  paragraphxSmall: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminLight, // smallprint page bottom
      fontSize: fontSize.xsmall,
      lineHeight: lineHeight.xsmall,
    },
  },
  paragraphLargeAlt: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminMedium, // Nav Links Mobile with Images
      fontSize: fontSize.large,
      lineHeight: lineHeight.large,
      fontWeight: fontWeight.semiBold,
    },
  },
  paragraphMedium: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminLight, // Nav Links Desktop
      fontSize: fontSize.medium,
      lineHeight: lineHeight.medium,
      fontWeight: fontWeight.medium,
    },
  },
  paragraphSmall: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminLight, // Nav Links Desktop
      fontSize: fontSize.small,
      lineHeight: lineHeight.small,
    },
  },
  paragraphMediumAlt: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminMedium, // various links in submenus
      fontSize: fontSize.medium,
      lineHeight: lineHeight.medium,
      fontWeight: fontWeight.semiBold,
    },
  },
  paragraphSmallAlt: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminMedium, // various links in submenus
      fontSize: fontSize.small,
      lineHeight: lineHeight.small,
    },
  },
  paragraphXSmall: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.book, // smallprint page bottom
      fontSize: fontSize.xsmall,
      lineHeight: lineHeight.xsmall,
      fontWeight: fontWeight.medium,
    },
  },
  labelLight: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminLight, // Form label
      fontSize: fontSize.medium,
      lineHeight: lineHeight.medium,
      fontWeight: fontWeight.medium,
    },
  },
  labelMedium: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminSemiBold, // desktop nav 2nd level (skincare - kits)
      fontSize: fontSize.medium,
      lineHeight: lineHeight.medium,
      fontWeight: fontWeight.medium,
    },
  },
  labelRegular: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminRegular, // desktop nav 2nd level (skincare - kits)
      fontSize: fontSize.medium,
      lineHeight: lineHeight.medium,
    },
  },
  labelSmall: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminRegular, // close button
      fontSize: fontSize.xsmall,
      lineHeight: lineHeight.small,
      fontWeight: fontWeight.medium,
    },
  },
  labelXSmall: {
    $type: TYPOGRAPHY,
    $value: {
      fontFamily: font.acuminRegular, // nav icon button
      fontSize: fontSize.xxsmall,
      lineHeight: lineHeight.small,
      fontWeight: fontWeight.medium,
    },
  },
}

type TypographyValueType = {
  fontFamily: keyof typeof font
  fontSize: keyof typeof fontSize
  lineHeight: keyof typeof lineHeight
  fontWeight?: keyof typeof fontWeight
}

const extractTypography = (token): TypographyValueType | null => {
  if (token.$type === TYPOGRAPHY) {
    return token.$value
  }
  return null
}

type TypographyKeyType = keyof typeof typographyTokens
type TypographyType = {
  [key in TypographyKeyType]: TypographyValueType
}

export const typographyValues = (Object.fromEntries(
  Object.entries(typographyTokens).map(([key, value]) => [
    key,
    extractTypography(value),
  ])
) ?? {}) as TypographyType
